import React, { useEffect, useState, useMemo } from "react";
import Papa from "papaparse";
import { useAuth0 } from "@auth0/auth0-react";
import { useParams } from "react-router-dom";

import { PageLayout } from "../components/page-layout";
import FinancialMetricsTable from "../components/financials-table";
import StarsComponent from "../components/stars-component";
import MetricsComponent from "../components/metrics-component";
import PriceIncomeChart from "../components/price-income-chart";

const StockPage = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { user } = useAuth0();
  const { ticker } = useParams();

  const [csvArray, setCsvArray] = useState([]);
  const [apiData, setApiData] = useState(null);
  const [valOneYear, setValOneYear] = useState(null);
  const [temPermissao, setTemPermissao] = useState(false);

  /* ---------------- PORTFOLIO ---------------- */
  const storageKey = `portfolio_${user?.email}`;
  const [savedTickers, setSavedTickers] = useState([]);
  const [successMsg, setSuccessMsg] = useState("");

  /* ---------------- AUTH PERMISSION ---------------- */
  const verificarPermissao = async () => {
    try {
      const token = await getAccessTokenSilently();
      const payload = JSON.parse(atob(token.split(".")[1]));
      const permitido = payload.permissions?.includes("read:asx");
      setTemPermissao(permitido);
    } catch (err) {
      console.error("Permission error:", err);
    }
  };

  useEffect(() => {
    verificarPermissao();
  }, []);

  /* ---------------- LOAD CSV ---------------- */
  useEffect(() => {
    const csvPath =
      "https://finance-files-servless-fabio.s3.us-east-1.amazonaws.com/stocks_loaded.csv";

    Papa.parse(csvPath, {
      download: true,
      header: false,
      complete: (result) => {
        const data = result.data.flat();
        setCsvArray(data);
      },
    });
  }, []);

  /* ---------------- LOAD PORTFOLIO ---------------- */
  useEffect(() => {
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      setSavedTickers(JSON.parse(saved));
    }
  }, []);

  /* ---------------- LOAD API DATA ---------------- */
  useEffect(() => {
    if (
      (csvArray.length > 0 && csvArray.includes(ticker.toUpperCase())) ||
      temPermissao
    ) {
      fetch(
        `https://36zb8ptdo4.execute-api.us-east-1.amazonaws.com/Prod/get/stock/${ticker.toUpperCase()}`
      )
        .then((res) => res.json())
        .then((json) => setApiData(json))
        .catch((err) => console.error("API error:", err));
    }

    if (temPermissao) {
      fetch(
        `https://36zb8ptdo4.execute-api.us-east-1.amazonaws.com/Prod/calculateReturn/${ticker.toUpperCase()}/220d`
      )
        .then((res) => res.json())
        .then((json) => setValOneYear(json.return))
        .catch((err) => console.error("API error:", err));
    } else {
      setValOneYear("Only Members. Contact: asxstockgraphs@gmail.com");
    }
  }, [csvArray, ticker, temPermissao]);

  /* ---------------- ADD TO PORTFOLIO ---------------- */
  const addToPortfolio = () => {
    const symbol = ticker.toUpperCase();

    if (savedTickers.includes(symbol)) {
      setSuccessMsg("This stock is already in your portfolio.");
      return;
    }

    const updated = [...savedTickers, symbol];
    setSavedTickers(updated);
    localStorage.setItem(storageKey, JSON.stringify(updated));

    setSuccessMsg("Stock successfully added to portfolio.");
    setTimeout(() => setSuccessMsg(""), 3000);
  };

  /* ---------------- GRAPH DATA ---------------- */
  const dataGraph = useMemo(() => {
    if (!apiData) return [];

    const financials = apiData.financial_data.financials ?? [];
    const prices = apiData.historicPrices ?? {};

    const lastFinancialYear = Math.max(...financials.map(f => f.Year));

    return financials
      .sort((a, b) => a.Year - b.Year)
      .map((f) => ({
        year: f.Year,
        netIncome: f.netIncome,
        price: f.Year === lastFinancialYear
          ? Math.round(apiData.lastPrice * 100) / 100
          : prices[String(f.Year)] ?? null,
      }));
  }, [apiData]);

  /* ---------------- ACCESS CONTROL ---------------- */
  const temTicker = csvArray.includes(ticker.toUpperCase());
  const podeVer = temPermissao || temTicker;
  const bloqueado = !podeVer && apiData == null;
  const carregando = !apiData;

  if (bloqueado) {
    return (
      <PageLayout>
        <p>Only available for members. Contact: asxstockgraphs@gmail.com</p>
      </PageLayout>
    );
  }

  if (carregando) {
    return (
      <PageLayout>
        <p>Loading...</p>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <br />

      <StarsComponent
        ticker={ticker.toUpperCase()}
        apiData={apiData}
        showStars={temTicker}
      />

      <PriceIncomeChart
        ticker={ticker.toUpperCase()}
        dataGraph={dataGraph}
      />

      <br />

      <MetricsComponent
        ticker={ticker.toUpperCase()}
        apiData={apiData}
        valOneYear={valOneYear}
        temPermissao={temPermissao}
      />

      {/* -------- ADD TO PORTFOLIO BUTTON -------- */}
      <div style={{ margin: "20px 0" }}>
        <button
          onClick={addToPortfolio}
          disabled={savedTickers.includes(ticker.toUpperCase())}
          style={{
            padding: "10px 16px",
            backgroundColor: "#1e88e5",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "bold",
          }}
        >
          {savedTickers.includes(ticker.toUpperCase())
            ? "Already in Portfolio"
            : "Add to Portfolio"}
        </button>

        {successMsg && (
          <p style={{ color: "green", marginTop: "10px" }}>
            {successMsg}
          </p>
        )}
      </div>

      <FinancialMetricsTable
        data={apiData.financial_data.financials}
        currency="AUD"
        locale="en-AU"
        marginIsPercentage={true}
        sortDesc={true}
      />
    </PageLayout>
  );
};

export default StockPage;
